import { useEffect, useState, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useStateValue } from "../../StateProvider";
import { getCartTotal } from "../../reducer";
import "./Checkout.css";
import { nigerianStates } from "../../Components/Assets/Assets";
import CheckoutTotal from "./CheckoutTotal";
import {
  ChevronLeft,
  Lock,
  ShieldCheck,
  CreditCard,
  Truck,
} from "lucide-react";
import { toast } from "react-toastify";
import checkLogo from "../../Components/Assets/images/tb_logo.png";
import { useTitle } from "../../hooks/useTitle";
import Loading from "../../Loading";
import { tablesDB, DB_ID, ORDERS_TABLE, PRODUCTS_TABLE, USERS_TABLE } from "../../appwrite";
import { ID } from "appwrite";

function Checkout() {
  useTitle("Checkout - TrendBay");
  const [{ cart, user }, dispatch] = useStateValue();
  const navigate = useNavigate();
  const [orderTotal, setOrderTotal] = useState(0);
  const [shippingSameAsBilling, setShippingSameAsBilling] = useState(false);

  const [shippingInfo, setShippingInfo] = useState(() => {
    const saved = localStorage.getItem("shippingInfo");
    return saved
      ? JSON.parse(saved)
      : {
          fullName: "",
          email: user?.email || "",
          address: "",
          phoneNumber: "",
          city: "",
          state: "",
          country: "Nigeria",
        };
  });

  const [billingInfo, setBillingInfo] = useState(() => {
    const saved = localStorage.getItem("billingInfo");
    return saved
      ? JSON.parse(saved)
      : {
          fullName: "",
          email: user?.email || "",
          address: "",
          phoneNumber: "",
          city: "",
          state: "",
          country: "Nigeria",
        };
  });

  const [isProcessing, setIsProcessing] = useState(false);
  const [subaccountCode, setSubaccountCode] = useState(null);

  const setShippingField = (field, value) =>
    setShippingInfo((p) => ({ ...p, [field]: value }));
  const setBillingField = (field, value) =>
    setBillingInfo((p) => ({ ...p, [field]: value }));

  useEffect(() => {
    localStorage.setItem("shippingInfo", JSON.stringify(shippingInfo));
  }, [shippingInfo]);
  useEffect(() => {
    localStorage.setItem("billingInfo", JSON.stringify(billingInfo));
  }, [billingInfo]);

  const cleanForAppwrite = (value) => {
    if (value instanceof Date) return value.toISOString();
    if (Array.isArray(value))
      return value.map(cleanForAppwrite).filter((v) => v !== undefined);
    if (value && typeof value === "object") {
      const out = {};
      Object.keys(value).forEach((k) => {
        const v = cleanForAppwrite(value[k]);
        if (v !== undefined) out[k] = v;
      });
      return out;
    }
    if (value === undefined) return undefined;
    return value;
  };

  const orderTotalNumber = useMemo(() => {
    const n = Number(orderTotal);
    return Number.isFinite(n) ? n : 0;
  }, [orderTotal]);
  const orderTotalKobo = useMemo(
    () => Math.round(orderTotalNumber * 100),
    [orderTotalNumber],
  );

  // Fetch seller subaccount code for split payment
  useEffect(() => {
    if (!cart || cart.length === 0) return;
    const sellerIds = [...new Set(cart.map((i) => i.sellerId).filter(Boolean))];
    if (sellerIds.length !== 1) { setSubaccountCode(null); return; }
    tablesDB.getRow({ databaseId: DB_ID, tableId: USERS_TABLE, rowId: sellerIds[0] })
      .then((doc) => setSubaccountCode(doc.subaccount_code || null))
      .catch(() => setSubaccountCode(null));
  }, [cart]);

  useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }
    if (!user.isVerified) {
      navigate("/account/verify-email");
      return;
    }
  }, [user, navigate]);

  const handleShippingSameAsBilling = () => {
    setShippingSameAsBilling((prev) => {
      const next = !prev;
      if (next) {
        setBillingInfo({ ...shippingInfo });
      } else {
        setBillingInfo({
          fullName: "",
          email: user?.email || "",
          address: "",
          phoneNumber: "",
          city: "",
          state: "",
          country: "Nigeria",
        });
      }
      return next;
    });
  };

  const generateOrderNumberFromId = (id) => {
    const numeric = id.replace(/\D/g, "");
    return Number(numeric.slice(1, 7));
  };

  const handlePaymentSuccess = async (reference) => {
    if (!isFormValid()) {
      toast.warn("Please fill in all required fields.");
      return;
    }
    if (!user?.$id) {
      toast.warn("You must be logged in to place an order.");
      return;
    }
    if (!cart?.length) {
      toast.warn("Your cart is empty.");
      return;
    }

    try {
      setIsProcessing(true);

      const productValidations = [];
      for (const item of cart) {
        try {
          const product = await tablesDB.getRow({
            databaseId: DB_ID,
            tableId: PRODUCTS_TABLE,
            rowId: item.id,
          });
          productValidations.push({ product, item });
          if (product?.category === "reup-vault" && product?.sold)
            throw new Error(
              `"${product?.productName || "Item"}" has already been sold.`,
            );
        } catch (error) {
          if (error.code === 404)
            throw new Error(`Product "${item.title}" not found.`);
          throw error;
        }
      }

      const order_id = ID.unique();
      const orderNumber = generateOrderNumberFromId(order_id);

      for (const { product } of productValidations) {
        if (product?.category === "reup-vault") {
          await tablesDB.updateRow({
            databaseId: DB_ID,
            tableId: PRODUCTS_TABLE,
            rowId: product.$id,
            data: {
              sold: true,
              available: false,
              soldAt: new Date().toISOString(),
            },
          });
        }
      }

      const enrichedCart = productValidations.map(({ product, item }) => {
        const base = cart.find((c) => c.id === item.id) || {};
        return cleanForAppwrite({
          ...base,
          sellerId: product?.sellerId ?? "",
          sellerName: product?.sellerName ?? "",
        });
      });
      const cartAsStringArray = enrichedCart.map((item) =>
        JSON.stringify(item),
      );
      const shippingInfoStringArray = [JSON.stringify(shippingInfo)];
      const billingSource = shippingSameAsBilling ? shippingInfo : billingInfo;
      const billingInfoStringArray = [JSON.stringify(billingSource)];

      const orderPayload = cleanForAppwrite({
        orderId: order_id,
        userId: user?.$id,
        cart: cartAsStringArray,
        orderTotal: Number.isFinite(orderTotalNumber) ? orderTotalNumber : 0,
        orderTotalKobo: Number.isFinite(orderTotalKobo) ? orderTotalKobo : 0,
        shippingInfo: shippingInfoStringArray,
        billingInfo: billingInfoStringArray,
        paymentReference: reference,
        paymentStatus: "paid",
        fulfillmentStatus: "pending",
        fulfilled: false,
        fulfillmentStatusUpdatedAt: null,
        orderNumber,
        numberUsed: true,
      });

      await tablesDB.createRow({
        databaseId: DB_ID,
        tableId: ORDERS_TABLE,
        rowId: order_id,
        data: orderPayload,
      });

      // Fire-and-forget emails — don't block checkout on email failure
      const WORKER = "https://email-api.trendbayds.com";
      if (WORKER) {
        try {
          const sendEmail = (payload) =>
            fetch(`${WORKER}/send`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(payload),
            }).catch(() => {});

          // Parse buyer info
          const buyer = shippingInfo;
          const parsedItems = enrichedCart;

          // 1. Order confirmation → buyer
          sendEmail({
            type: "order_confirmation",
            payload: {
              buyerEmail: buyer.email,
              buyerName: buyer.fullName,
              orderNumber,
              orderTotal: orderTotalNumber,
              transactionRef: reference?.reference || reference?.trxref || reference,
              items: parsedItems.map((i) => ({
                title: i.title,
                quantity: i.quantity,
                price: i.price,
              })),
              shippingAddress: {
                address: buyer.address,
                city: buyer.city,
                state: buyer.state,
              },
            },
          });

          // 2. New order alert → each unique seller (grouped by sellerId)
          const bySeller = {};
          for (const item of parsedItems) {
            if (!item.sellerId) continue;
            if (!bySeller[item.sellerId]) {
              bySeller[item.sellerId] = {
                sellerName: item.sellerName || "Seller",
                items: [],
              };
            }
            bySeller[item.sellerId].items.push({
              title: item.title,
              quantity: item.quantity,
              price: item.price,
            });
          }
          const sellerIds = Object.keys(bySeller);
          for (const sid of sellerIds) {
            try {
              const sellerDoc = await tablesDB.getRow({
                databaseId: DB_ID,
                tableId: USERS_TABLE,
                rowId: sid,
              });
              if (sellerDoc?.email) {
                sendEmail({
                  type: "new_order_alert",
                  payload: {
                    sellerEmail: sellerDoc.email,
                    sellerName: bySeller[sid].sellerName,
                    orderNumber,
                    items: bySeller[sid].items,
                    buyerFirstName: buyer.fullName?.split(" ")[0] || "A customer",
                  },
                });
              }
            } catch {}
          }
        } catch {}
      }

      dispatch({ type: "EMPTY_CART" });
      localStorage.setItem("orderReference", reference);
      navigate(`/thank-you?reference=${reference}`);
    } catch (err) {
      console.error("Order creation failed:", err);
      toast.error(err?.message || "There was an issue processing your order.");
    } finally {
      setIsProcessing(false);
    }
  };

  useEffect(() => {
    const total = getCartTotal(cart);
    const tax = total * 0.075;
    setOrderTotal((total + tax).toFixed(2));
  }, [cart]);

  const isFormValid = () => {
    if (
      !shippingInfo.fullName ||
      !shippingInfo.address ||
      !shippingInfo.phoneNumber ||
      !shippingInfo.city ||
      !shippingInfo.state
    )
      return false;
    if (!shippingSameAsBilling) {
      if (
        !billingInfo.fullName ||
        !billingInfo.address ||
        !billingInfo.phoneNumber ||
        !billingInfo.city ||
        !billingInfo.state
      )
        return false;
    }
    return true;
  };

  if (isProcessing)
    return (
      <div className="co-loading">
        <Loading />
      </div>
    );

  return (
    <div className="co-page">
      <form onSubmit={(e) => e.preventDefault()}>
        <div className="co-layout">
          {/* Left - forms */}
          <div className="co-forms">
            {/* Header bar */}
            <div className="co-top">
              <Link to="/cart" className="co-back">
                <ChevronLeft size={16} /> Back
              </Link>
              <div className="co-top-center">
                <img src={checkLogo} className="co-logo" alt="TrendBay" />
                <p className="co-label">CHECKOUT</p>
              </div>
              <div className="co-top-secure">
                <Lock size={13} /> Secure
              </div>
            </div>

            {/* Step breadcrumb */}
            <div className="co-steps">
              <span className="co-step co-step--done">Cart</span>
              <span className="co-step-sep">›</span>
              <span className="co-step co-step--active">Shipping</span>
              <span className="co-step-sep">›</span>
              <span className="co-step">Payment</span>
            </div>

            {/* Form body */}
            <div className="co-body">
              {/* 01 Shipping */}
              <div className="co-section">
                <h5 className="co-section-title">
                  <span className="co-section-num">1</span>
                  Shipping Address
                </h5>
                <div className="co-form">
                  <div className="co-form-row">
                    <div className="co-field">
                      <label className="co-field-label">Full Name</label>
                      <input
                        type="text"
                        value={shippingInfo.fullName}
                        placeholder="e.g. Amaka Obi"
                        className="co-input"
                        onChange={(e) =>
                          setShippingField("fullName", e.target.value)
                        }
                        required
                      />
                    </div>
                    <div className="co-field">
                      <label className="co-field-label">Email</label>
                      <input
                        type="email"
                        value={shippingInfo.email}
                        className="co-input"
                        readOnly
                        disabled
                      />
                    </div>
                    <div className="co-field">
                      <label className="co-field-label">Phone Number</label>
                      <input
                        type="text"
                        value={shippingInfo.phoneNumber}
                        placeholder="e.g. 08012345678"
                        className="co-input"
                        onChange={(e) =>
                          setShippingField("phoneNumber", e.target.value)
                        }
                        required
                      />
                    </div>
                  </div>
                  <div className="co-field">
                    <label className="co-field-label">Street Address</label>
                    <input
                      type="text"
                      value={shippingInfo.address}
                      placeholder="House number, street name"
                      className="co-input"
                      onChange={(e) =>
                        setShippingField("address", e.target.value)
                      }
                      required
                    />
                  </div>
                  <div className="co-form-row">
                    <div className="co-field">
                      <label className="co-field-label">City</label>
                      <input
                        type="text"
                        value={shippingInfo.city}
                        placeholder="e.g. Lagos Island"
                        className="co-input"
                        onChange={(e) =>
                          setShippingField("city", e.target.value)
                        }
                      />
                    </div>
                    <div className="co-field">
                      <label className="co-field-label">State</label>
                      <select
                        value={shippingInfo.state}
                        onChange={(e) =>
                          setShippingField("state", e.target.value)
                        }
                        className="co-input co-select"
                        required
                      >
                        <option value="" disabled>
                          Select State
                        </option>
                        <option value="Lagos">Lagos</option>
                        <option value="Abuja">Abuja</option>
                      </select>
                    </div>
                    <div className="co-field">
                      <label className="co-field-label">Country</label>
                      <select
                        className="co-input co-select"
                        value={shippingInfo.country}
                        onChange={() => {}}
                      >
                        <option value="Nigeria">Nigeria</option>
                        <option disabled>United Kingdom (Coming Soon)</option>
                        <option disabled>Canada (Coming Soon)</option>
                        <option disabled>United States (Coming Soon)</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* 02 Billing */}
              <div className="co-section">
                <h5 className="co-section-title">
                  <span className="co-section-num">2</span>
                  Billing Address
                </h5>
                <label className="co-checkbox">
                  <input
                    type="checkbox"
                    checked={shippingSameAsBilling}
                    onChange={handleShippingSameAsBilling}
                  />
                  <span>Same as shipping address</span>
                </label>
                {!shippingSameAsBilling && (
                  <div className="co-form">
                    <div className="co-form-row">
                      <div className="co-field">
                        <label className="co-field-label">Full Name</label>
                        <input
                          type="text"
                          value={billingInfo.fullName}
                          placeholder="e.g. Amaka Obi"
                          className="co-input"
                          onChange={(e) =>
                            setBillingField("fullName", e.target.value)
                          }
                          required
                        />
                      </div>
                      <div className="co-field">
                        <label className="co-field-label">Email</label>
                        <input
                          type="email"
                          value={billingInfo.email}
                          className="co-input"
                          readOnly
                          disabled
                        />
                      </div>
                      <div className="co-field">
                        <label className="co-field-label">Phone Number</label>
                        <input
                          type="text"
                          value={billingInfo.phoneNumber}
                          placeholder="e.g. 08012345678"
                          className="co-input"
                          onChange={(e) =>
                            setBillingField("phoneNumber", e.target.value)
                          }
                          required
                        />
                      </div>
                    </div>
                    <div className="co-field">
                      <label className="co-field-label">Street Address</label>
                      <input
                        type="text"
                        value={billingInfo.address}
                        placeholder="House number, street name"
                        className="co-input"
                        onChange={(e) =>
                          setBillingField("address", e.target.value)
                        }
                        required
                      />
                    </div>
                    <div className="co-form-row">
                      <div className="co-field">
                        <label className="co-field-label">City</label>
                        <input
                          type="text"
                          value={billingInfo.city}
                          placeholder="e.g. Lagos Island"
                          className="co-input"
                          onChange={(e) =>
                            setBillingField("city", e.target.value)
                          }
                        />
                      </div>
                      <div className="co-field">
                        <label className="co-field-label">State</label>
                        <select
                          value={billingInfo.state}
                          onChange={(e) =>
                            setBillingField("state", e.target.value)
                          }
                          className="co-input co-select"
                          required
                        >
                          <option value="" disabled>
                            Select State
                          </option>
                          <option value="Lagos">Lagos</option>
                          <option value="Abuja">Abuja</option>
                          <optgroup label="Other States">
                            {nigerianStates.map((s) => (
                              <option key={s} value={s}>
                                {s}
                              </option>
                            ))}
                          </optgroup>
                        </select>
                      </div>
                      <div className="co-field">
                        <label className="co-field-label">Country</label>
                        <select
                          className="co-input co-select"
                          value={billingInfo.country}
                          onChange={() => {}}
                        >
                          <option value="Nigeria">Nigeria</option>
                          <option disabled>United Kingdom (Coming Soon)</option>
                          <option disabled>Canada (Coming Soon)</option>
                          <option disabled>United States (Coming Soon)</option>
                        </select>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            {/* /co-body */}

            {/* Trust bar */}
            <div className="co-trust">
              <div className="co-trust-item">
                <ShieldCheck size={14} /> SSL Encrypted
              </div>
              <div className="co-trust-item">
                <CreditCard size={14} /> Secured by Paystack
              </div>
              <div className="co-trust-item">
                <Truck size={14} /> Fast Delivery
              </div>
            </div>
          </div>
          {/* /co-forms */}

          {/* Right - summary */}
          <div className="co-summary">
            <CheckoutTotal
              user={user}
              onPaymentSuccess={handlePaymentSuccess}
              email={shippingInfo.email}
              amount={orderTotal}
              name={shippingInfo.fullName}
              phone={shippingInfo.phoneNumber}
              isFormValid={isFormValid}
              subaccountCode={subaccountCode}
            />
          </div>
        </div>
      </form>
    </div>
  );
}

export default Checkout;
